// Reset-password page — single LoginWindow that swaps between two
// inner forms based on whether the URL carries a `?token=…`:
//   - no token (entry point):  ResetEmailForm (request the reset link)
//   - has token (email link):  ResetPasswordsForm (set a new password)
//
// OAuth buttons stay visible in either mode so the user can pivot to
// "sign in with Google" if they remember after starting the reset.
//
// State ownership: LoginWindow declares its own `state` block — the
// page's own `state` is unreachable to the form's `onInput` /
// `onSubmit` handlers, which all resolve `s` to the closest state
// owner (LoginWindow). So every reset-password field has to live on
// LoginWindow.state, and the URL-param hydration in onInit has to
// target the same scope.

import { withLiveQuery } from '../../functions/bootRedirectQuery.js'
import { authPageTheme } from './theme.js'
export const resetPassword = {
  extends: 'Page',
  metadata: (el) => ({ title: el.call('polyglot', 'auth.page.resetPassword.title') }),
  flow: 'y',
  align: 'center center',
  padding: 'D C',
  width: '100%',
  height: '100dvh',
  maxWidth: 'none',
  ...authPageTheme,

  LoginWindow: {
    // White-label host: the branded card shape and the org lockup, the same
    // as signup and the branded sign-in (components/WhitelabelAuthWindow.js) —
    // inert on the platform.
    extends: ['WhitelabelAuthWindow', 'LoginWindow'],
    WhitelabelLockup: {
      order: '-1',
      subscribeTo: ['whitelabelBrand'],
      hide: (el, s) => !(s.root && s.root.whitelabelBrand),
      Wordmark: { tag: 'div', color: 'title' },
      Tagline: { color: 'caption' }
    },
    // Override LoginWindow's default `{ loading, identifier, password }`
    // state with the fields the reset flow actually uses. Without this
    // override, `s.email`, `s.token`, `s.sent`, etc. read as undefined
    // inside onSubmit because LoginWindow's default shape doesn't have
    // them — the reset request hits the SDK with email='' and silently
    // shows the "Email required" path, OR the token-based confirm path
    // never fires because `s.token` is always undefined.
    state: {
      loading: false,
      sent: false,
      touched: false,
      email: '',
      password: '',
      repeatPassword: '',
      token: null,
      passwordTouched: false,
      repeatPasswordTouched: false
    },

    // Hydrate token + email from the URL query string. Runs on the
    // LoginWindow itself so `s` is the same scope the rest of the form
    // reads from.
    onInit: (el, s) => {
      if (typeof window === 'undefined') return
      const url = new URL(window.location.href)
      // #2301 — must be `update`, not `quietUpdate`: the ResetPasswordsForm
      // `if: (el,s) => !!s.token` factory has to re-evaluate once the token
      // lands, otherwise it stays hidden and the user only sees the email
      // request step (looks like a plain sign-in page).
      s.update({
        token: url.searchParams.get('token') || null,
        email: url.searchParams.get('email') || ''
      })
    },

    ModalHeader: {
      Hgroup: {
        H: {
          text: (el, s) =>
            el.call(
              'polyglot',
              s.token ? 'auth.resetPassword.heading.set' : 'auth.resetPassword.heading.request'
            )
        },
        P: null
      }
    },
    Flex: {
      // Step 1: request a reset email. Hidden once the user lands here
      // via an email link that carried the token. Also hidden after a
      // successful send so the SentNotice gets the column to itself.
      ResetEmailForm: {
        if: (el, s) => !s.token && !s.sent
      },
      // Step 2: set the new password. Hidden until a token is present.
      ResetPasswordsForm: {
        if: (el, s) => !!s.token
      },
      // After a successful reset request, swap the form for a notice.
      // (Sits at the same level so the LoginWindow Flex layout doesn't
      // collapse when the form is hidden.)
      SentNotice: {
        if: (el, s) => s.sent && !s.token,
        flow: 'y',
        gap: 'A',
        Body: {
          tag: 'p',
          color: 'caption',
          fontSize: 'A',
          text: (el, s) =>
            s.email
              ? el
                  .call('polyglot', 'auth.resetPassword.sentNotice')
                  .replace('{email}', () => s.email)
              : el.call('polyglot', 'auth.resetPassword.sentNoticeNoEmail')
        }
      }
    },
    onSubmit: async (ev, el, s) => {
      ev.preventDefault()
      // Branch on which mode the form is currently in. Two onSubmit
      // closures was simpler in the old inline page — here we share
      // one because LoginWindow has tag:'form' and there's only one
      // submit handler per form element.
      if (s.token) {
        return await _submitNewPassword(el, s)
      }
      return await _submitRequestReset(el, s)
    }
  }
}

const _submitRequestReset = async (el, s) => {
  // Read from state first, then fall back to a live DOM read in case
  // signal-tracking dropped a keystroke (matches the signin.js
  // pattern — password managers and rapid typing can race the state
  // mirror). Without the fallback the form silently shows the
  // "Email required" path even though the user clearly typed.
  const node = el.node
  const email = (s.email || node?.querySelector?.('input[autocomplete="username"]')?.value || '')
    .trim()
    .toLowerCase()
  if (!email) {
    el.call('openNotification', {
      title: el.call('polyglot', 'auth.resetPassword.emailRequired.title'),
      message: el.call('polyglot', 'auth.resetPassword.emailRequired.message'),
      type: 'error'
    })
    s.update({ touched: true })
    return
  }
  s.update({ loading: true })
  try {
    await el.sdk.requestPasswordReset(email)
    s.update({ sent: true, loading: false, email })
    el.call('openNotification', {
      title: el.call('polyglot', 'auth.resetPassword.linkSent.title'),
      message: el.call('polyglot', 'auth.resetPassword.linkSent.message').replace('{email}', email),
      type: 'success'
    })
  } catch (e) {
    console.error('[reset-password] requestPasswordReset failed:', e)
    s.update({ loading: false })
    el.call('openNotification', {
      title: el.call('polyglot', 'auth.resetPassword.sendFailed'),
      message: e?.message || String(e) || el.call('polyglot', 'auth.login.tryAgain'),
      type: 'error'
    })
  }
}

const _submitNewPassword = async (el, s) => {
  // Same state + DOM-fallback pattern as the email field — the two
  // password inputs lose their state mirror under rapid typing /
  // password-manager autofill, and reading the DOM at submit time
  // catches values the signal tracker dropped.
  const node = el.node
  const pwInputs = node?.querySelectorAll?.('input[type="password"]') || []
  const password = s.password || pwInputs[0]?.value || ''
  const repeat = s.repeatPassword || pwInputs[1]?.value || ''
  if (!password || !repeat) {
    el.call('openNotification', {
      title: el.call('polyglot', 'auth.resetPassword.missingFields.title'),
      message: el.call('polyglot', 'auth.resetPassword.missingFields.message'),
      type: 'error'
    })
    s.update({ passwordTouched: true, repeatPasswordTouched: true })
    return
  }
  if (password !== repeat) {
    el.call('openNotification', {
      title: el.call('polyglot', 'auth.resetPassword.mismatchTitle'),
      message: el.call('polyglot', 'auth.resetPassword.mismatchMessage'),
      type: 'error'
    })
    s.update({ repeatPasswordTouched: true })
    return
  }
  s.update({ loading: true })
  try {
    await el.sdk.confirmPasswordReset(s.token, password)
    el.call('openNotification', {
      title: el.call('polyglot', 'auth.resetPassword.updated.title'),
      message: el.call('polyglot', 'auth.resetPassword.updated.message'),
      type: 'success'
    })
    // withLiveQuery carries the live query/hash onto `/signin` —
    // tickets/sonnet.md "auth-page redirects still drop the query". A
    // bare `/signin` silently dropped any param riding alongside the
    // reset (e.g. `?next=`, `?claim=credits` from an earlier hop).
    setTimeout(() => el.router(withLiveQuery('/signin'), el.getRoot()), 250)
  } catch (e) {
    console.error('[reset-password] confirmPasswordReset failed:', e)
    s.update({ loading: false })
    el.call('openNotification', {
      title: el.call('polyglot', 'auth.resetPassword.updateFailed'),
      message: e?.message || String(e) || el.call('polyglot', 'auth.login.tryAgain'),
      type: 'error'
    })
  }
}
